import { osloDateKey } from "../misc/osloDate";
import type { Deal, DealStage, DealStatus } from "../types";

// Status er adskilt fra stadium (pipeline v2.1, kap. 5): en mulighet er åpen
// og står i et stadium, eller den er vunnet, tapt eller diskvalifisert.
// Rader lest før migreringen mangler status; de regnes som åpne.
export const dealStatus = (deal: Pick<Deal, "status">): DealStatus =>
  deal.status ?? "open";

export const isDealOpen = (deal: Pick<Deal, "status">) =>
  dealStatus(deal) === "open";

export const isDealWon = (deal: Pick<Deal, "status">) =>
  dealStatus(deal) === "won";

export const isDealLost = (deal: Pick<Deal, "status">) =>
  dealStatus(deal) === "lost";

export const isDealClosed = (deal: Pick<Deal, "status">) => !isDealOpen(deal);

export const DEAL_STATUSES: DealStatus[] = [
  "open",
  "won",
  "lost",
  "disqualified",
];

// Sannsynligheten (0–1) som gjelder for muligheten: en overstyrt verdi på
// muligheten (prosent) går foran stadiets.
export const dealProbability = (
  deal: Pick<Deal, "probability" | "stage" | "status">,
  dealStages: DealStage[],
): number => {
  if (isDealWon(deal)) return 1;
  if (!isDealOpen(deal)) return 0;
  if (deal.probability != null) return deal.probability / 100;
  return dealStages.find((s) => s.value === deal.stage)?.probability ?? 0;
};

// Engangsverdien. amount er det gamle feltet og fylles fra engangsverdien av
// databasen; eldre rader kan bare ha amount.
export const dealOneTimeValue = (
  deal: Pick<Deal, "one_time_value" | "amount">,
): number => Number(deal.one_time_value ?? deal.amount ?? 0);

export const dealMonthlyValue = (deal: Pick<Deal, "monthly_value">): number =>
  Number(deal.monthly_value ?? 0);

// Årsverdi: engangsbeløp pluss tolv måneder av det månedlige beløpet.
export const dealAnnualValue = (
  deal: Pick<Deal, "one_time_value" | "amount" | "monthly_value">,
): number => dealOneTimeValue(deal) + 12 * dealMonthlyValue(deal);

// Rad fra tabellen deal_stages → stadium slik appen bruker det. Databasen
// lagrer sannsynlighet i prosent; appen regner med 0–1.
export type DealStageRow = Omit<DealStage, "probability"> & {
  probability: number;
};

export const dealStageFromRow = (row: DealStageRow): DealStage => ({
  ...row,
  probability: (row.probability ?? 0) / 100,
  required_fields: row.required_fields ?? [],
});

export const dealStageToRow = (stage: DealStage): DealStageRow => ({
  ...stage,
  probability: Math.round((stage.probability ?? 0) * 100),
});

const LEGACY_LOSS_REASONS: Record<string, string> = {
  pris: "pris_budsjett",
  konkurrent: "valgte_konkurrent",
  utsatt: "timing",
  stille: "ingen_respons",
};

// Speiler databasetriggeren deals_before_write for demodata (FakeRest), som
// ikke har triggere: datoer ved avslutning og gjenåpning, stadium ved
// avslutning, verdier og eldre skrivinger av stage = 'won' / 'lost'.
export const applyDealWriteRules = (
  data: Partial<Deal>,
  previous?: Partial<Deal>,
): Partial<Deal> => {
  const next: Partial<Deal> = { ...previous, ...data };
  const now = new Date().toISOString();
  const today = osloDateKey();

  if (next.stage === "won" || next.stage === "lost") {
    next.status = next.stage;
    next.stage = previous?.stage ?? null;
  } else if (next.stage === "delayed") {
    next.status = "open";
    next.stage = "opportunity";
  } else if (
    data.stage != null &&
    previous?.status &&
    previous.status !== "open" &&
    data.status == null
  ) {
    next.status = "open";
  }
  next.status = next.status ?? "open";
  if (next.loss_reason && LEGACY_LOSS_REASONS[next.loss_reason]) {
    next.loss_reason = LEGACY_LOSS_REASONS[next.loss_reason];
  }

  if (data.one_time_value != null) {
    next.amount = Math.round(Number(data.one_time_value));
  } else if (data.amount != null) {
    next.one_time_value = Number(data.amount);
  }
  next.one_time_value = Number(next.one_time_value ?? next.amount ?? 0);
  next.monthly_value = Number(next.monthly_value ?? 0);
  next.annual_value = next.one_time_value + 12 * next.monthly_value;
  // Avtaleverdi over bindingstiden (pakke 4), generert i databasen.
  next.contract_value =
    next.binding_months == null
      ? null
      : next.one_time_value + next.binding_months * next.monthly_value;

  if (next.stage !== previous?.stage) next.stage_changed_at = now;

  const wasOpen = (previous?.status ?? "open") === "open";
  // Råtning (pakke 2): stadieendring og gjenåpning teller som aktivitet.
  if (
    !previous ||
    next.stage !== previous.stage ||
    (!wasOpen && next.status === "open")
  ) {
    next.last_activity_at = now;
  }
  if (previous && wasOpen && next.status !== "open") {
    next.closed_at = now;
    next.stage_at_close = next.stage ?? previous.stage ?? null;
    if (next.status === "won") next.won_date = next.won_date ?? today;
    if (next.status === "lost") next.lost_date = next.lost_date ?? today;
    next.reopen_reason = null;
  } else if (previous && !wasOpen && next.status === "open") {
    next.closed_at = null;
    next.won_date = null;
    next.lost_date = null;
    next.loss_reason = null;
    next.stage_at_close = null;
    // Tapsfeltene tømmes ved gjenåpning (pakke 3); loggen beholder dem.
    next.competitor_id = null;
    next.loss_comment = null;
    next.loss_learning = null;
    next.revisit_date = null;
  }
  return next;
};
